import { decryptRawBytes, encryptRawBytes } from './aes.ts';

export type MediaEncryptionEnvelope = {
  v: 1;
  alg: 'A256GCM';
  iv: string;
  mimeType: string;
};

export function parseMediaEncryption(value: unknown): MediaEncryptionEnvelope | null {
  if (!value || typeof value !== 'object') return null;
  const parsed = value as Record<string, unknown>;
  if (
    parsed.v !== 1 ||
    parsed.alg !== 'A256GCM' ||
    typeof parsed.iv !== 'string' ||
    typeof parsed.mimeType !== 'string' ||
    !parsed.mimeType.startsWith('image/')
  ) {
    return null;
  }
  return {
    v: 1,
    alg: 'A256GCM',
    iv: parsed.iv,
    mimeType: parsed.mimeType
  };
}

export function isMediaEncryptionEnvelope(value: unknown): boolean {
  return parseMediaEncryption(value) !== null;
}

function safePhotoName(name: string): string {
  const base = (name || 'photo').replace(/\.[^.]+$/, '');
  const cleaned = base.replace(/[^\w.-]+/g, '_').slice(0, 80);
  return `${cleaned || 'photo'}.enc`;
}

export async function encryptPhotoForUpload(
  threadKey: CryptoKey,
  file: Blob & { name?: string; type?: string }
): Promise<{ file: File; mediaEncryption: MediaEncryptionEnvelope }> {
  const mimeType = file.type && file.type.startsWith('image/') ? file.type : 'image/jpeg';
  const plaintext = new Uint8Array(await readBlobBytes(file));
  const wrapped = await encryptRawBytes(threadKey, plaintext);
  const cipherFile = new File([wrapped.ciphertext], safePhotoName(file.name || 'photo'), {
    type: 'application/octet-stream'
  });
  return {
    file: cipherFile,
    mediaEncryption: {
      v: 1,
      alg: 'A256GCM',
      iv: wrapped.iv,
      mimeType
    }
  };
}

async function readBlobBytes(file: Blob): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error || new Error('Could not read photo bytes'));
    reader.readAsArrayBuffer(file);
  });
}

export async function decryptPhotoBytes(
  threadKey: CryptoKey,
  envelope: MediaEncryptionEnvelope,
  ciphertext: Uint8Array
): Promise<Uint8Array> {
  return decryptRawBytes(threadKey, envelope.iv, ciphertext);
}

export async function decryptPhotoToObjectUrl(
  threadKey: CryptoKey,
  ciphertext: Uint8Array,
  envelope: MediaEncryptionEnvelope
): Promise<string> {
  const plaintext = await decryptPhotoBytes(threadKey, envelope, ciphertext);
  const blob = new Blob([plaintext], { type: envelope.mimeType });
  return URL.createObjectURL(blob);
}
